// src/components/ui/FloatingInput.jsx
import { forwardRef, useState } from "react";
import {
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";

const FloatingInput = forwardRef(function FloatingInput(
  {
    label,
    error,
    success,
    type = "text",
    className = "",
    value = "",
    required = false,
    dir = "ltr", // ltr | rtl (plus simple que language)
    ...props
  },
  ref
) {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);

  const isRTL = dir === "rtl";
  const hasValue = Boolean(value && value.toString().trim());
  const inputType =
    type === "password" && showPassword ? "text" : type;

  return (
    <div className="w-full space-y-1" dir={dir}>
      <div className="relative">
        {/* INPUT */}
        <input
          ref={ref}
          type={inputType}
          value={value}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder=" "
          className={`
            peer w-full h-12 px-4 pt-5
            rounded-lg
            bg-white dark:bg-gray-900
            border
            text-sm
            text-gray-900 dark:text-gray-100
            focus:outline-none focus:ring-2
            transition
            ${
              error
                ? "border-red-500 focus:ring-red-500/20"
                : success
                ? "border-green-500 focus:ring-green-500/20"
                : "border-gray-300 dark:border-gray-700 focus:border-blue-600 focus:ring-blue-600/20"
            }
            ${isRTL ? "text-right" : "text-left"}
            ${className}
          `}
          {...props}
        />

        {/* FLOATING LABEL */}
        <label
          className={`
            absolute px-1 bg-white dark:bg-gray-900
            text-sm text-gray-500 dark:text-gray-400
            transition-all pointer-events-none
            ${
              focused || hasValue
                ? "top-1 text-xs text-blue-600 dark:text-blue-400"
                : "top-1/2 -translate-y-1/2"
            }
            ${isRTL ? "right-3" : "left-3"}
          `}
        >
          {label}
          {required && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </label>

        {/* PASSWORD TOGGLE */}
        {type === "password" && (
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className={`
              absolute top-1/2 -translate-y-1/2
              text-gray-400 hover:text-gray-600
              dark:text-gray-500 dark:hover:text-gray-300
              ${isRTL ? "left-4" : "right-4"}
            `}
          >
            {showPassword ? (
              <EyeSlashIcon className="w-5 h-5" />
            ) : (
              <EyeIcon className="w-5 h-5" />
            )}
          </button>
        )}

        {/* STATUS ICON */}
        {success && (
          <CheckCircleIcon
            className={`
              w-5 h-5 text-green-500 absolute top-1/2 -translate-y-1/2
              ${isRTL ? "left-4" : "right-4"}
            `}
          />
        )}

        {!success && error && (
          <ExclamationCircleIcon
            className={`
              w-5 h-5 text-red-500 absolute top-1/2 -translate-y-1/2
              ${isRTL ? "left-4" : "right-4"}
            `}
          />
        )}
      </div>

      {/* ERROR MESSAGE */}
      {error && (
        <p
          className={`text-xs text-red-600 dark:text-red-400 ${
            isRTL ? "text-right" : "text-left"
          }`}
        >
          {error}
        </p>
      )}
    </div>
  );
});

export default FloatingInput;
